import { useMemo } from 'react';
import chroma from 'chroma-js';

import { useAppSelector } from '@/store/hooks';
import { getWorkingProject } from '@/store/slices/project/selectors';
import { selectThemePacks } from '@/store/slices/templates/selectors';
import { CLASSIC_COLOR_PACK_ID, COLOR_VARIABLES_LIST_V2_MAP, EPaletteModes } from '@/types/theme-packs.types';

const useGetWorkingProjectThemeColorsList = () => {
  const workingProject = useAppSelector(getWorkingProject);
  const themePacks = useAppSelector(selectThemePacks);

  const themePackId = workingProject?.settings?.design?.colors?.color_pack || CLASSIC_COLOR_PACK_ID;

  const themePack = useMemo(() => {
    if (!themePacks) return null;
    return themePacks.find((themePack) => themePack.color_pack === themePackId);
  }, [themePackId, themePacks]);

  const themeMode = workingProject?.settings?.design?.color_theme || themePack?.theme_mode || EPaletteModes.Light;

  const presetColors = useMemo(() => {
    // get theme colors
    if (themePack) {
      return Object.entries(themePack?.colors?.[themeMode] || {})
        .map(([colorName, color]) => {
          if (!COLOR_VARIABLES_LIST_V2_MAP[colorName]) return;
          if (Array.isArray(color) && typeof color[0] === 'string') {
            const hex = color[0];
            const opacity = color[1] || 1;
            if (chroma.valid(hex)) {
              return chroma(hex).alpha(opacity).hex();
            }
            return;
          }
        })
        .filter(Boolean) as string[];
    }
    return [];
  }, [themePack, themeMode]);

  return presetColors;
};

export default useGetWorkingProjectThemeColorsList;
